import { useCallback, useEffect, useState } from "react";
import type { PrinterInput, PrinterRecordDto } from "@delta/shared";
import { Link } from "react-router-dom";
import { useLang } from "../../i18n";
import { useSession } from "../../lib/session";
import { api } from "../../lib/api";
import { errorText, fieldErrorsOf } from "../../lib/errors";
import { Icon } from "../../components/Icon";
import { PageHead, Shell } from "../../components/Shell";
import { ListView, type Column } from "../../components/ListView";
import { Blocked, Confirm, Field, Modal } from "../../components/Modal";
import { useToast } from "../../components/Toast";
import { LockedButton, lockTag } from "./common";

// Settings › Printers, ported from the prototype (erp-settings.js "/printers"): name, IP, port, hard margins and paper
// width. Configuration only: Delta sends nothing to a printer yet, so the prototype's "Test print" is not offered and
// nothing here says that a printer works with Delta. A printer that users print tickets on cannot be deleted.

const IP = /^(25[0-5]|2[0-4][0-9]|1[0-9][0-9]|[1-9]?[0-9])(\.(25[0-5]|2[0-4][0-9]|1[0-9][0-9]|[1-9]?[0-9])){3}$/;
type Form = { name: string; ip: string; port: string; marginX: string; marginY: string; paperWidth: string };

function PrinterForm({ p, onClose, onSaved }: { p: PrinterRecordDto | null; onClose(): void; onSaved(x: PrinterRecordDto, created: boolean): void }) {
  const { t } = useLang();
  const plain = (x: string) => x.replace(/\.?0+$/, "") || "0";
  const [v, setV] = useState<Form>(() => p ? { name: p.name, ip: p.ip ?? "", port: String(p.port), marginX: plain(p.marginX), marginY: plain(p.marginY), paperWidth: plain(p.paperWidth) }
    : { name: "", ip: "", port: "9100", marginX: "0", marginY: "0", paperWidth: "80" });
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [alert, setAlert] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (k: keyof Form, x: string) => { setV((o) => ({ ...o, [k]: x })); setErrs((e) => ({ ...e, [k]: "" })); };
  async function save() {
    const e: Record<string, string> = {};
    if (!v.name.trim()) e.name = t("requiredField");
    if (!v.ip.trim()) e.ip = t("requiredField"); else if (!IP.test(v.ip.trim())) e.ip = t("ipFormat");
    const port = Number(v.port);
    if (!/^\d+$/.test(v.port.trim()) || port < 1 || port > 65535) e.port = t("portFormat");
    for (const k of ["marginX", "marginY"] as const) if (!(Number(v[k]) >= 0) || !v[k].trim()) e[k] = t("positiveNumber");
    if (!(Number(v.paperWidth) > 0)) e.paperWidth = t("positiveNumber");
    setErrs(e);
    if (Object.keys(e).length) { setAlert(Object.values(e)[0]!); return; }
    setBusy(true); setAlert(null);
    const body: PrinterInput = { name: v.name, ip: v.ip.trim(), port, marginX: v.marginX.trim(), marginY: v.marginY.trim(), paperWidth: v.paperWidth.trim() };
    try { onSaved(p ? await api<PrinterRecordDto>("PUT", `/printers/${p.id}`, body) : await api<PrinterRecordDto>("POST", "/printers", body), !p); }
    catch (err) { setErrs(fieldErrorsOf(t, err)); setAlert(errorText(t, err)); }
    finally { setBusy(false); }
  }
  const num = (k: keyof Form, id: string, label: string, req = false, hint?: string) => (
    <Field label={label} req={req} error={errs[k]} htmlFor={id} hint={hint}>
      <input id={id} className="control num" style={{ textAlign: "start" }} dir="ltr" inputMode="decimal" value={v[k]} onChange={(e) => set(k, e.target.value)} autoComplete="off" />
    </Field>
  );
  return (
    <Modal title={t(p ? "editPrinter" : "newPrinter")} wide onClose={onClose} onSubmit={() => void save()} alert={alert} busy={busy}>
      <Field label={t("name")} req error={errs.name} htmlFor="pr-name"><input id="pr-name" className="control" value={v.name} maxLength={200} onChange={(e) => set("name", e.target.value)} autoComplete="off" /></Field>
      <Field label="IP" req error={errs.ip} htmlFor="pr-ip"><input id="pr-ip" className="control num" style={{ textAlign: "start" }} dir="ltr" placeholder="192.168.1.50" value={v.ip} onChange={(e) => set("ip", e.target.value)} autoComplete="off" /></Field>
      {num("port", "pr-port", t("port"), true)}
      {num("marginX", "pr-mx", t("hardMarginX"))}
      {num("marginY", "pr-my", t("hardMarginY"))}
      {num("paperWidth", "pr-width", t("paperWidth"), true, t("paperWidthHint"))}
      <div className="field full"><div className="dep-note" data-testid="printer-config-note"><Icon name="info" /><span>{t("printerConfigOnly")}</span></div></div>
    </Modal>
  );
}

export function Printers() {
  const { t } = useLang();
  const s = useSession();
  const toast = useToast();
  const [rows, setRows] = useState<PrinterRecordDto[] | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [editing, setEditing] = useState<PrinterRecordDto | "new" | null>(null);
  const [deleting, setDeleting] = useState<PrinterRecordDto | null>(null);
  const [blocked, setBlocked] = useState<PrinterRecordDto | null>(null);
  const load = useCallback(async () => {
    setFailed(null);
    try { setRows(await api<PrinterRecordDto[]>("GET", `/printers${filters.name ? "?q=" + encodeURIComponent(filters.name) : ""}`)); } catch (e) { setFailed(errorText(t, e)); }
  }, [filters.name, t]);
  useEffect(() => { void load(); }, [load]);
  async function remove(p: PrinterRecordDto) {
    try { await api("DELETE", `/printers/${p.id}`); toast(t("deletedOk", { x: p.name })); } catch (e) { toast(errorText(t, e)); }
    await load();
  }
  const mm = (x: string) => x.replace(/\.?0+$/, "") || "0";
  const columns: Column<PrinterRecordDto>[] = [
    { k: "name", label: t("name"), cell: (p) => <span className="cell-name"><Icon name="printer" /> {p.name}</span>, csv: (p) => p.name },
    { k: "ip", label: "IP", cell: (p) => (p.ip ? <span className="num" dir="ltr">{p.ip}</span> : <span className="muted">–</span>), csv: (p) => p.ip ?? "" },
    { k: "port", label: t("port"), cell: (p) => <span className="num">{p.port}</span>, csv: (p) => p.port },
    { k: "mx", label: t("hardMarginX"), end: true, cell: (p) => <span className="num">{mm(p.marginX)}</span>, csv: (p) => p.marginX },
    { k: "my", label: t("hardMarginY"), end: true, cell: (p) => <span className="num">{mm(p.marginY)}</span>, csv: (p) => p.marginY },
    { k: "width", label: t("paperWidth"), end: true, cell: (p) => <span className="num">{mm(p.paperWidth)} mm</span>, csv: (p) => p.paperWidth },
    { k: "users", label: t("users"), end: true, cell: (p) => <span className="num">{p.usersCount}</span>, csv: (p) => p.usersCount }
  ];
  const newBtn = s.can("printerAdd") ? <button className="btn btn-primary" onClick={() => setEditing("new")}><Icon name="plus" />{t("newPrinter")}</button>
    : <LockedButton label={t("newPrinter")} title={t("noPermissionFor", { p: "printerAdd" })} />;
  return (
    <Shell navKey="/printers">
      <PageHead crumbs={[[t("home"), "/home"], [t("settings")], [t("printers")]]} title={t("printersSetting")}
        actions={<Link className="btn" to="/printers/this-pc" data-testid="printers-this-pc"><Icon name="printer" />{t("printersThisPc")}</Link>} />
      <ListView rows={rows} error={failed} columns={columns} file="printers" testId="printers-table" onRefresh={() => void load()}
        search={[{ k: "name", label: t("name"), type: "text" }]} filters={filters} onFilters={setFilters}
        quick={(p) => `${p.name} ${p.ip ?? ""}`} newButton={newBtn}
        empty={{ icon: "printer", title: t("noResults"), body: t("noResultsBody") }}
        actions={(p) => [
          { label: t("edit"), icon: "edit", run: () => setEditing(p), ...(s.can("printerUpdate") ? {} : lockTag(t)) },
          { label: "", sep: true },
          { label: t("delete"), icon: "trash", danger: true, run: () => (p.usersCount ? setBlocked(p) : setDeleting(p)), ...(s.can("printerDelete") ? {} : lockTag(t)) }
        ]} />
      {editing && <PrinterForm p={editing === "new" ? null : editing} onClose={() => setEditing(null)}
        onSaved={(p, created) => { setEditing(null); toast(t(created ? "addedOk" : "updatedOk", { x: p.name })); void load(); }} />}
      {deleting && <Confirm title={t("confirmDelete")} body={t("deleteWarn")} okLabel={t("yesSure")} onClose={() => setDeleting(null)} onOk={() => { const p = deleting; setDeleting(null); void remove(p); }} />}
      {blocked && <Blocked title={t("cantDelete")} body={t("printerInUse", { n: blocked.usersCount })} onClose={() => setBlocked(null)} />}
    </Shell>
  );
}
